/**
 * AvatarDisplay Component
 *
 * Presence/avatar rules:
 * 1) Scope: show users only for the current patient across both Patient page and
 *    Editable Document page.
 * 2) Current user: always show star badge on your own avatar.
 * 3) Patient-page presence: users on patient pages render as blue/inactive.
 * 4) Document-page presence: users connected to document websocket render with
 *    their synced cursor color.
 * 5) Transition behavior:
 *    - Patient -> Document: avatar starts as blue, then becomes cursor color after
 *      websocket/collaboration readiness.
 *    - Document leave (grace ~10s): keep collaboration color, lightened toward white,
 *      dashed ring — then blue inactive or disappear per context.
 * 6) Editable document navbar: do not show users who are only on the patient
 *    details page (path patient/... without a .json doc).
 */

import React, { useState, useEffect } from 'react';
import { IconButton, Tooltip, VStack, Text, HStack, Badge } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKeyboard, faStar, faClock } from '@fortawesome/free-solid-svg-icons';
import globalPresenceManager from '../utils/globalPresenceManager';
import { dbgLog } from '../utils/appDebugFlags';

const avLog = (...args) => dbgLog('avatar-display', ...args);

/** Normalize DB / ISO dates to YYYY-MM-DD for visit-scoped presence (recurring same-patient rows). */
function normalizeVisitDate(d) {
  if (d == null || d === '') return '';
  const s = String(d).trim();
  if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
}

/**
 * Med-a / storage paths often embed the visit folder: .../2026/2026-04-10/Name-id.json
 * Use this when remote presence rows omit schedule_date (older tabs or merge gaps).
 */
function deriveVisitDateFromDocumentPath(path) {
  const p = String(path || '');
  const m = p.match(/(\d{4}-\d{2}-\d{2})/);
  if (!m) return '';
  return normalizeVisitDate(m[1]);
}

function effectiveUserVisitDay(user, path) {
  const fromPresence = normalizeVisitDate(user?.schedule_date);
  if (fromPresence) return fromPresence;
  return deriveVisitDateFromDocumentPath(path);
}

/** Stale (grace period) accent — dashed ring + clock badge. */
const STALE_YELLOW_BORDER = '#eab308';
const STALE_BADGE_BG = '#fde047';

/** Blend a hex color toward white — stale doc avatars stay on-brand but look “faded”. */
function lightenHexTowardWhite(hex, amount = 0.48) {
  const raw = String(hex || "").trim();
  if (!raw.startsWith("#")) return raw || "#3b82f6";
  const h = raw.length >= 7 ? raw.slice(1, 7) : raw.slice(1);
  if (h.length !== 6) return raw;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return raw;
  const L = (c) => Math.round(c + (255 - c) * amount);
  return `#${L(r).toString(16).padStart(2, "0")}${L(g).toString(16).padStart(2, "0")}${L(b).toString(16).padStart(2, "0")}`;
}

function userMatchesPatientContext(user, normalizedPatientId, patientNameLower, scheduleDate) {
  const path = String(user?.document_path || "");
  const userPatientId = String(user?.patient_id || "").trim();
  const isPatientPagePath = path.startsWith(`patient/${normalizedPatientId}`);
  const isDocumentForPatient =
    path.includes(`/${normalizedPatientId}/`) ||
    path.includes(`-${normalizedPatientId}.json`) ||
    path.includes(`/${normalizedPatientId}.json`);
  const matchesId = userPatientId === normalizedPatientId;
  const matchesName =
    patientNameLower.length > 0 && path.toLowerCase().includes(patientNameLower);
  const baseMatch =
    matchesId || isPatientPagePath || isDocumentForPatient || matchesName;
  if (!baseMatch) return false;

  const viewerDay = normalizeVisitDate(scheduleDate);
  if (!viewerDay) return true;

  const userDay = effectiveUserVisitDay(user, path);
  if (userDay) {
    return userDay === viewerDay;
  }

  // Visit-scoped viewer but we could not resolve the remote user's day (no presence date,
  // no YYYY-MM-DD in path). Allow only "strong" patient matches — not name substring alone
  // (same display name / token on another visit or another patient file leaks via matchesName).
  const nameOnlyWeak =
    matchesName && !matchesId && !isPatientPagePath && !isDocumentForPatient;
  if (nameOnlyWeak) return false;

  return true;
}

function getPatientMatchReasons(user, normalizedPatientId, patientNameLower, scheduleDate) {
  const path = String(user?.document_path || "");
  const userPatientId = String(user?.patient_id || "").trim();
  const reasons = {
    matchesId: userPatientId === normalizedPatientId,
    isPatientPagePath: path.startsWith(`patient/${normalizedPatientId}`),
    isDocumentForPatient:
      path.includes(`/${normalizedPatientId}/`) ||
      path.includes(`-${normalizedPatientId}.json`) ||
      path.includes(`/${normalizedPatientId}.json`),
    matchesName:
      patientNameLower.length > 0 && path.toLowerCase().includes(patientNameLower),
  };
  const viewerDay = normalizeVisitDate(scheduleDate);
  const userDayFromPath = deriveVisitDateFromDocumentPath(path);
  const effectiveUserDay = effectiveUserVisitDay(user, path);
  return {
    ...reasons,
    viewerScheduleDate: scheduleDate || null,
    viewerDay: viewerDay || null,
    userScheduleDate: user?.schedule_date ?? null,
    normalizedPresenceSchedule: normalizeVisitDate(user?.schedule_date) || null,
    derivedVisitDayFromPath: userDayFromPath || null,
    effectiveUserVisitDay: effectiveUserDay || null,
    matchesCurrentPatient: userMatchesPatientContext(
      user,
      normalizedPatientId,
      patientNameLower,
      scheduleDate,
    ),
    matchesScheduleVisit:
      !viewerDay ||
      (!!effectiveUserDay && effectiveUserDay === viewerDay),
  };
}

/**
 * Pick the better presence row for routing avatars. Editable .json doc paths must beat
 * patient-only `patient/uuid` rows — otherwise the Supabase snapshot overwrites Yjs/collab
 * rows and document navbar hides the user (hidePatientPageOnlyUsersOnDocumentView).
 */
function presenceMergeScore(u) {
  const p = String(u?.document_path || '');
  let s =
    (p ? 3 : 0) +
    (String(u?.patient_id || '').trim() || String(u?.patient_name || '').trim() ? 3 : 0);
  if (p.endsWith('.json')) {
    s += 12;
  } else if (p.startsWith('patient/') && !p.includes('.json')) {
    s += 2;
  }
  return s;
}

function getUsersFromPresenceChannelSnapshot() {
  try {
    const channel = globalPresenceManager?.presenceChannel;
    if (!channel || typeof channel.presenceState !== 'function') return [];
    const state = channel.presenceState();
    const rows = Object.values(state || {}).flat();
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const byUser = new Map();
    rows.forEach((row) => {
      if (!row?.user_id) return;
      const candidate = {
        user_id: row.user_id,
        name: row.name || 'Anonymous',
        email: row.email || 'Guest',
        is_typing: row.is_typing || false,
        cursor_active: row.cursor_active || false,
        last_activity: row.last_activity || new Date().toISOString(),
        document_path: row.document_path || '',
        patient_id: row.patient_id || '',
        patient_name: row.patient_name || '',
        schedule_date: row.schedule_date || '',
        source: 'presence-channel-snapshot',
      };
      const existing = byUser.get(row.user_id);
      if (!existing || presenceMergeScore(candidate) >= presenceMergeScore(existing)) {
        byUser.set(row.user_id, candidate);
      }
    });
    return Array.from(byUser.values());
  } catch (_) {
    return [];
  }
}

function mergePresenceSources(primaryUsers, snapshotUsers) {
  const merged = new Map();

  const pickDisconnectedAt = (a, b) => {
    if (a?.isStale === true && a?.disconnectedAt != null) return a.disconnectedAt;
    if (b?.isStale === true && b?.disconnectedAt != null) return b.disconnectedAt;
    return a?.disconnectedAt ?? b?.disconnectedAt;
  };

  // Snapshot first, then primary (global activeUsers + websocket awareness) so equal scores
  // favor the richer map; presenceMergeScore ensures .json beats patient/ for same user.
  [...snapshotUsers, ...primaryUsers].forEach((u) => {
    if (!u?.user_id) return;
    const existing = merged.get(u.user_id);
    if (!existing || presenceMergeScore(u) >= presenceMergeScore(existing)) {
      const isStale = u.isStale === true || existing?.isStale === true;
      merged.set(u.user_id, {
        ...u,
        isStale: isStale ? true : false,
        ...(isStale ? { disconnectedAt: pickDisconnectedAt(u, existing) } : {}),
      });
    } else {
      const isStale = existing.isStale === true || u.isStale === true;
      merged.set(u.user_id, {
        ...existing,
        isStale: isStale ? true : false,
        ...(isStale ? { disconnectedAt: pickDisconnectedAt(existing, u) } : {}),
      });
    }
  });
  return Array.from(merged.values());
}

/** Broadcast-only rows can linger briefly; on patient page hide ghosts unless ping is very fresh or presence lists them. */
const PATIENT_BROADCAST_ONLY_MAX_AGE_MS = 3500;

function filterBroadcastGhostOnPatientPage(
  users,
  viewerContext,
  currentUserId,
  presenceSnapshotUserIds,
  currentPatientId,
  patientName,
  scheduleDate
) {
  if (viewerContext !== 'patient') return users;
  const now = Date.now();
  const normalizedPatientId = currentPatientId ? String(currentPatientId).trim() : '';
  const patientNameLower = (patientName || '').toLowerCase().trim();
  return users.filter((u) => {
    if (!currentUserId || u.user_id === currentUserId) return true;
    if (u.__presenceConfirmed === true) return true;
    if (u.source === 'websocket-awareness') return true;
    if (presenceSnapshotUserIds?.has?.(u.user_id)) return true;
    // Remote user is in an editable .json for this patient — not a broadcast ghost; keeping them
    // avoids avatars vanishing ~3.5s after Patient→Document when presenceState() is flaky.
    const path = String(u?.document_path || '');
    if (
      normalizedPatientId &&
      path.endsWith('.json') &&
      userMatchesPatientContext(u, normalizedPatientId, patientNameLower, scheduleDate)
    ) {
      return true;
    }
    if (u.source === 'broadcast-ping' || u.__broadcastReceivedAt != null) {
      const age = now - (u.__broadcastReceivedAt || 0);
      return age <= PATIENT_BROADCAST_ONLY_MAX_AGE_MS;
    }
    return true;
  });
}

/** Document navbar: hide others who are only on patient details (not in a .json doc). */
function hidePatientPageOnlyUsersOnDocumentView(users, viewerContext, currentPatientId, currentUserId) {
  if (viewerContext !== 'document' || !currentPatientId) return users;
  const pid = String(currentPatientId).trim();
  return users.filter((u) => {
    if (currentUserId && u.user_id === currentUserId) return true;
    const path = String(u?.document_path || '');
    const onlyPatientDetails =
      path === `patient/${pid}` ||
      (path.startsWith(`patient/${pid}`) && !path.includes('.json'));
    if (onlyPatientDetails) return false;
    return true;
  });
}

function applyPresenceViewFilters(
  users,
  viewerContext,
  selfId,
  snapIds,
  currentPatientId,
  patientName,
  scheduleDate
) {
  return hidePatientPageOnlyUsersOnDocumentView(
    filterBroadcastGhostOnPatientPage(
      users,
      viewerContext,
      selfId,
      snapIds,
      currentPatientId,
      patientName,
      scheduleDate
    ),
    viewerContext,
    currentPatientId,
    selfId
  );
}

function getFilteredUsersForContext(allUsers, currentPatientId, patientName, scheduleDate) {
  if (!currentPatientId) return allUsers;
  const normalizedPatientId = String(currentPatientId).trim();
  const patientNameLower = (patientName || "").toLowerCase().trim();

  // Use deterministic local filtering to avoid cross-page context differences in
  // globalPresenceManager.getActiveUsersForPatient().
  let filteredUsers = allUsers.filter((user) =>
    userMatchesPatientContext(user, normalizedPatientId, patientNameLower, scheduleDate)
  );

  const userMap = new Map();
  filteredUsers.forEach(user => userMap.set(user.user_id, user));

  filteredUsers = Array.from(userMap.values());

  // Fallback: if strict matching yields nothing, use conservative heuristics
  // for THIS patient only (never show unrelated patients).
  if (filteredUsers.length === 0 && allUsers.length > 0) {
    const conservativeMatches = allUsers.filter((user) => {
      return userMatchesPatientContext(user, normalizedPatientId, patientNameLower, scheduleDate);
    });
    if (conservativeMatches.length > 0) {
      return conservativeMatches;
    }
  }

  return filteredUsers;
}

export default function AvatarDisplay({
  onUserAvatarClick,
  showTypingIndicator = true,
  maxAvatars = 5,
  userColors = new Map(),
  currentPatientId = null,
  patientName = null,
  /** YYYY-MM-DD for this scheduled visit — scopes avatars when the same patient has multiple visits (e.g. recurring). */
  scheduleDate = null,
  showAsInactive = false, // Optional forced inactive mode
  viewerContext = 'document', // 'patient' or 'document'
  /** When on the editable doc navbar: file path ending in .json — shows collaboration color for "You" even before presence catches up */
  viewerDocumentPath = null,
}) {
  const [activeUsers, setActiveUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingRetryCount, setLoadingRetryCount] = useState(0);
  const mergePatientUnknownContextUsers = (allUsers, filteredUsers) => {
    if (viewerContext !== 'patient' || !currentPatientId) return filteredUsers;
    if (normalizeVisitDate(scheduleDate)) return filteredUsers;

    const normalizedPatientId = String(currentPatientId).trim();
    const hasExplicitCurrentPatientUser = allUsers.some((u) => {
      const path = String(u?.document_path || '');
      const pid = String(u?.patient_id || '').trim();
      return pid === normalizedPatientId || path.startsWith(`patient/${normalizedPatientId}`);
    });
    if (!hasExplicitCurrentPatientUser) return filteredUsers;

    const unknownContextUsers = allUsers.filter((u) => {
      const path = String(u?.document_path || '');
      const pid = String(u?.patient_id || '').trim();
      return path.length === 0 && pid.length === 0;
    });
    if (unknownContextUsers.length === 0) return filteredUsers;

    const merged = new Map();
    filteredUsers.forEach((u) => merged.set(u.user_id, u));
    unknownContextUsers.forEach((u) => {
      if (!merged.has(u.user_id)) merged.set(u.user_id, u);
    });
    return Array.from(merged.values());
  };

  useEffect(() => {
    const refreshPresenceSnapshot = () => {
      const channelSnap = getUsersFromPresenceChannelSnapshot();
      const snapIds = new Set(channelSnap.map((x) => x.user_id));
      const latestUsers = mergePresenceSources(
        globalPresenceManager.getActiveUsers(),
        channelSnap
      );
      const baseFilteredUsers = getFilteredUsersForContext(
        latestUsers,
        currentPatientId,
        patientName,
        scheduleDate
      );
      const mergedUnknown = mergePatientUnknownContextUsers(latestUsers, baseFilteredUsers);
      const selfId = globalPresenceManager.getCurrentUser()?.id || null;
      const latestFilteredUsers = applyPresenceViewFilters(
        mergedUnknown,
        viewerContext,
        selfId,
        snapIds,
        currentPatientId,
        patientName,
        scheduleDate
      );
      const latestCurrentUser = globalPresenceManager.getCurrentUser();
      setActiveUsers(latestFilteredUsers);
      setCurrentUser(latestCurrentUser);
      if (latestFilteredUsers.length > 0) {
        setLoadingRetryCount(0);
      }
    };

    // Subscribe to presence updates
    const unsubscribe = globalPresenceManager.subscribe(({ activeUsers }) => {
      const channelSnap = getUsersFromPresenceChannelSnapshot();
      const snapIds = new Set(channelSnap.map((x) => x.user_id));
      const mergedUsers = mergePresenceSources(
        activeUsers || [],
        channelSnap
      );
      const baseFilteredUsers = getFilteredUsersForContext(
        mergedUsers,
        currentPatientId,
        patientName,
        scheduleDate
      );
      const mergedUnknown = mergePatientUnknownContextUsers(mergedUsers, baseFilteredUsers);
      const selfId = globalPresenceManager.getCurrentUser()?.id || null;
      const filteredUsers = applyPresenceViewFilters(
        mergedUnknown,
        viewerContext,
        selfId,
        snapIds,
        currentPatientId,
        patientName,
        scheduleDate
      );
        
      // console.log('🔄 AvatarDisplay: Received presence update:', {
      //   currentPatientId,
      //   totalUsers: activeUsers.length,
      //   filteredUsers: filteredUsers.length,
      //   filteredUsersList: filteredUsers.map(u => ({ 
      //     name: u.name, 
      //     userId: u.user_id,
      //     patient_id: u.patient_id, 
      //     document_path: u.document_path,
      //     color: globalPresenceManager.getCachedUserColor(u.user_id)
      //   }))
      // });
        
      // Use filtered users so we only show users for the current patient
      setActiveUsers(filteredUsers);
      setCurrentUser(globalPresenceManager.getCurrentUser());
      if (filteredUsers.length > 0) {
        setLoadingRetryCount(0);
      }
    });

    // Get initial state + periodic safety refresh in case a realtime callback is missed.
    refreshPresenceSnapshot();
    const refreshInterval = setInterval(refreshPresenceSnapshot, 1500);
    const channelSnap = getUsersFromPresenceChannelSnapshot();
    const snapIds = new Set(channelSnap.map((x) => x.user_id));
    const allUsers = mergePresenceSources(
      globalPresenceManager.getActiveUsers(),
      channelSnap
    );
    const initialUsers = applyPresenceViewFilters(
      mergePatientUnknownContextUsers(
        allUsers,
        getFilteredUsersForContext(allUsers, currentPatientId, patientName, scheduleDate)
      ),
      viewerContext,
      globalPresenceManager.getCurrentUser()?.id || null,
      snapIds,
      currentPatientId,
      patientName,
      scheduleDate
    );
    const initialCurrentUser = globalPresenceManager.getCurrentUser();
    avLog('🔄 AvatarDisplay: Initial state:', {
      currentPatientId,
      activeUsers: initialUsers.length,
      currentUser: initialCurrentUser?.name,
      users: initialUsers.map(u => ({ 
        name: u.name, 
        userId: u.user_id,
        patient_id: u.patient_id, 
        document_path: u.document_path,
        color: globalPresenceManager.getCachedUserColor(u.user_id)
      }))
    });
    setActiveUsers(initialUsers);
    setCurrentUser(initialCurrentUser);

    return () => {
      unsubscribe();
      clearInterval(refreshInterval);
    };
  }, [currentPatientId, patientName, viewerContext, scheduleDate]);

  // Keep re-checking presence briefly while AvatarDisplay is empty.
  useEffect(() => {
    if (activeUsers.length > 0) return undefined;

    let attempts = 0;
    const maxAttempts = 8; // short bootstrap window
    const intervalId = setInterval(() => {
      attempts += 1;
      const chSnap = getUsersFromPresenceChannelSnapshot();
      const sIds = new Set(chSnap.map((x) => x.user_id));
      const refreshedAllUsers = globalPresenceManager.getActiveUsers();
      const mergedRefreshedUsers = mergePresenceSources(
        refreshedAllUsers,
        chSnap
      );
      const refreshedFilteredUsers = applyPresenceViewFilters(
        mergePatientUnknownContextUsers(
          mergedRefreshedUsers,
          getFilteredUsersForContext(
            mergedRefreshedUsers,
            currentPatientId,
            patientName,
            scheduleDate
          )
        ),
        viewerContext,
        globalPresenceManager.getCurrentUser()?.id || null,
        sIds,
        currentPatientId,
        patientName,
        scheduleDate
      );
      const refreshedCurrentUser = globalPresenceManager.getCurrentUser();

      setLoadingRetryCount(attempts);
      if (refreshedFilteredUsers.length > 0) {
        setActiveUsers(refreshedFilteredUsers);
        setCurrentUser(refreshedCurrentUser);
        setLoadingRetryCount(0);
        clearInterval(intervalId);
      } else if (attempts >= maxAttempts) {
        setLoadingRetryCount(0);
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [activeUsers.length, currentPatientId, patientName, viewerContext, scheduleDate]);

  // Enhanced debug logging for typing indicators
  // console.log('🔍 AvatarDisplay typing debug:', {
  //   totalUsers: activeUsers.length,
  //   typingUsers: typingUsers.length,
  //   allUsers: activeUsers.map(u => ({ 
  //     name: u.name, 
  //     is_typing: u.is_typing, 
  //     cursor_active: u.cursor_active,
  //     source: u.source,
  //     last_activity: u.last_activity
  //   })),
  //   typingUsersList: typingUsers.map(u => ({ 
  //     name: u.name, 
  //     is_typing: u.is_typing,
  //     source: u.source,
  //     last_activity: u.last_activity
  //   }))
  // });
  
  // Additional analysis for debugging
  const usersWithTypingTrue = activeUsers.filter(user => user.is_typing === true);
  const usersWithTypingUndefined = activeUsers.filter(user => user.is_typing === undefined);
  const usersWithTypingFalse = activeUsers.filter(user => user.is_typing === false);
  
  // console.log('🔍 Typing status analysis:', {
  //   typingTrue: usersWithTypingTrue.length,
  //   typingUndefined: usersWithTypingUndefined.length,
  //   typingFalse: usersWithTypingFalse.length,
  //   typingTrueUsers: usersWithTypingTrue.map(u => u.name),
  //   typingUndefinedUsers: usersWithTypingUndefined.map(u => u.name),
  //   typingFalseUsers: usersWithTypingFalse.map(u => u.name)
  // });
  const resolvedCurrentUser = currentUser || globalPresenceManager.getCurrentUser();
  const resolvedCurrentUserId = resolvedCurrentUser?.id || null;
  const isViewerPatientPage = viewerContext === 'patient';
  const viewerDocPath = String(viewerDocumentPath || "").trim();
  const hasCurrentUserInList =
    !!resolvedCurrentUserId && activeUsers.some((u) => u.user_id === resolvedCurrentUserId);
  const selfFallbackUser =
    resolvedCurrentUserId && !hasCurrentUserInList
      ? {
          user_id: resolvedCurrentUserId,
          name: resolvedCurrentUser?.name || "You",
          document_path: isViewerPatientPage
            ? `patient/${String(currentPatientId || "").trim()}`
            : viewerDocPath,
          patient_id: String(currentPatientId || "").trim(),
          schedule_date: normalizeVisitDate(scheduleDate) || "",
          is_typing: false,
          cursor_active: false,
          source: "self-fallback",
        }
      : null;
  const usersToRender = selfFallbackUser
    ? [...activeUsers, selfFallbackUser]
    : activeUsers;
  const typingUsers = usersToRender.filter(user => user.is_typing === true);

  return (
    <div className="navbar-user-icons">
      {/* Typing indicators */}
      {showTypingIndicator && !showAsInactive && typingUsers.length > 0 && (
        <VStack spacing={1} align="start" mr={4}>
          <HStack spacing={2}>
            <FontAwesomeIcon icon={faKeyboard} color="#10b981" />
            <Text fontSize="sm" color="green.600" fontWeight="medium">
              Typing:
            </Text>
          </HStack>
          <Text fontSize="xs" color="gray.600">
            {typingUsers.map(user => user.name).join(', ')}
          </Text>
        </VStack>
      )}

      {/* User avatars */}
      {usersToRender.length === 0 && (
        <div className="text-gray-500 text-sm mr-2 flex items-center">
          {loadingRetryCount > 0 ? "🔄 Loading users..." : "No other users"}
        </div>
      )}

      {usersToRender.length > 0 && (
        <>
          {usersToRender.length > maxAvatars ? (
            // Show first 5 avatars with "more" badge
            <>
              {usersToRender.slice(0, maxAvatars).map((user) => {
                const isOnPatientDetailsPage = !user.document_path || !user.document_path.endsWith('.json');
                const isStale = user.isStale === true;
                const isYouOnThisDocPage =
                  viewerContext === 'document' &&
                  viewerDocPath.length > 0 &&
                  resolvedCurrentUserId &&
                  user.user_id === resolvedCurrentUserId;
                // Inactive (blue) unless user is actively in document context for this patient.
                // Stale users keep stale state during grace window, then naturally become inactive.
                const shouldShowAsInactive =
                  showAsInactive ||
                  (!isStale &&
                    !isYouOnThisDocPage &&
                    (isOnPatientDetailsPage || (isViewerPatientPage && !user.document_path)));
                
                // Inactive = blue; active/stale = collaboration color (stale is lightened toward white).
                const localColor = userColors && userColors.get ? userColors.get(user.user_id) : null;
                const cachedColor = localColor || globalPresenceManager.getCachedUserColor(user.user_id);
                const baseCollaborationColor = shouldShowAsInactive ? "#3b82f6" : (cachedColor || "#3b82f6");
                const userColor =
                  isStale && !shouldShowAsInactive
                    ? lightenHexTowardWhite(baseCollaborationColor, 0.48)
                    : baseCollaborationColor;

                // Debug logging for color retrieval
                if (!cachedColor && !shouldShowAsInactive) {
                  avLog('⚠️ AvatarDisplay: No color found for user:', {
                    userId: user.user_id,
                    userName: user.name,
                    documentPath: user.document_path,
                    localColor,
                    cachedColor: globalPresenceManager.getCachedUserColor(user.user_id),
                    allCachedColors: Array.from(globalPresenceManager.getCachedUserColors().entries())
                  });
                }
                const isTyping = shouldShowAsInactive ? false : (user.is_typing === true);
                const hasCursor = shouldShowAsInactive ? false : (user.cursor_active === true);
                const isCurrentUser = resolvedCurrentUserId && user.user_id === resolvedCurrentUserId;

                return (
                  <Tooltip
                    key={user.user_id}
                    label={
                      <VStack spacing={1} align="start">
                        <Text>{user.name} {isCurrentUser && "(You)"}</Text>
                        {isStale && (
                          <Text fontSize="xs" color="gray.600">
                            Left document — may return (grace period)
                          </Text>
                        )}
                        {!shouldShowAsInactive && isTyping && !isStale && <Text fontSize="xs" color="green.300">Typing...</Text>}
                        {!shouldShowAsInactive && hasCursor && !isTyping && !isStale && <Text fontSize="xs" color="blue.300">Active</Text>}
                        {shouldShowAsInactive && <Text fontSize="xs" color="gray.400">Inactive</Text>}
                      </VStack>
                    }
                    openDelay={250}
                  >
                    <div className="my-2" style={{ position: 'relative' }}>
                      <div
                        style={
                          isStale && !shouldShowAsInactive
                            ? {
                                display: 'inline-flex',
                                lineHeight: 0,
                                borderRadius: '50%',
                                padding: '2px',
                                border: `2px dashed ${STALE_YELLOW_BORDER}`,
                                boxShadow: '0 0 10px rgba(234, 179, 8, 0.45)',
                              }
                            : { display: 'inline-flex', lineHeight: 0 }
                        }
                      >
                      <IconButton
                        size="md"
                        className="p-8"
                        fontSize="25px"
                        rounded="full"
                        variant="solid"
                        bg={userColor}
                        color="white"
                        onClick={() => {
                          const allUsersSnapshot = mergePresenceSources(
                            globalPresenceManager.getActiveUsers(),
                            getUsersFromPresenceChannelSnapshot()
                          );
                          const patientIdForLog = String(currentPatientId || '').trim();
                          const patientNameForLog = (patientName || '').toLowerCase().trim();
                          const stageBaseFiltered = getFilteredUsersForContext(
                            allUsersSnapshot,
                            currentPatientId,
                            patientName,
                            scheduleDate
                          );
                          const hasExplicitCurrentPatientUser = allUsersSnapshot.some((u) => {
                            const path = String(u?.document_path || '');
                            const pid = String(u?.patient_id || '').trim();
                            return pid === patientIdForLog || path.startsWith(`patient/${patientIdForLog}`);
                          });
                          const unknownContextUsers = allUsersSnapshot.filter((u) => {
                            const path = String(u?.document_path || '');
                            const pid = String(u?.patient_id || '').trim();
                            return path.length === 0 && pid.length === 0;
                          });
                          const matchDebug = allUsersSnapshot.map((u) => ({
                            userId: u.user_id,
                            name: u.name,
                            patientId: u.patient_id,
                            documentPath: u.document_path,
                            ...getPatientMatchReasons(u, patientIdForLog, patientNameForLog, scheduleDate),
                            includedInBaseFilter: stageBaseFiltered.some((x) => x.user_id === u.user_id),
                            includedInRenderedList: usersToRender.some((x) => x.user_id === u.user_id),
                            hasUnknownContext:
                              String(u?.document_path || '').length === 0 &&
                              String(u?.patient_id || '').trim().length === 0,
                            isStale: u.isStale === true,
                            cachedColor: globalPresenceManager.getCachedUserColor(u.user_id),
                          }));
                          console.groupCollapsed('🧪 [Avatar Click Debug]');
                          console.log('clickedUser', user);
                          console.log('viewer', {
                            viewerContext,
                            currentPatientId,
                            patientName,
                            scheduleDate,
                            viewerVisitDay: normalizeVisitDate(scheduleDate) || null,
                            resolvedCurrentUserId,
                          });
                          console.log(
                            'presenceHealth',
                            typeof globalPresenceManager.getPresenceHealth === 'function'
                              ? globalPresenceManager.getPresenceHealth()
                              : null,
                          );
                          console.log('renderState', {
                            activeUsersCount: activeUsers.length,
                            usersToRenderCount: usersToRender.length,
                            activeUsers,
                            usersToRender,
                            typingUsers,
                            userColors: Array.from(userColors?.entries?.() || []),
                          });
                          console.log('filterStages', {
                            allUsersCount: allUsersSnapshot.length,
                            baseFilteredCount: stageBaseFiltered.length,
                            baseFilteredUserIds: stageBaseFiltered.map((x) => x.user_id),
                            hasExplicitCurrentPatientUser,
                            unknownContextUsersCount: unknownContextUsers.length,
                          });
                          console.log('presenceSnapshot', matchDebug);
                          try {
                            console.table(matchDebug);
                          } catch (_) {
                            // console.table may be unavailable in some environments
                          }
                          console.groupEnd();
                          avLog('🖱️ Avatar clicked:', user, 'isStale:', isStale);
                          if (onUserAvatarClick) {
                            onUserAvatarClick(user);
                          }
                        }}
                        cursor="pointer"
                        opacity={1}
                        _hover={{
                          transform: 'scale(1.1)',
                          bg: userColor,
                          opacity: 1
                        }}
                        border={
                          shouldShowAsInactive
                            ? "none"
                            : isStale
                              ? "none"
                              : isTyping
                                ? "2px solid #10b981"
                                : hasCursor
                                  ? "2px solid #3b82f6"
                                  : "none"
                        }
                        boxShadow={
                          shouldShowAsInactive
                            ? "none"
                            : isStale
                              ? "none"
                              : isTyping
                                ? "0 0 10px rgba(16, 185, 129, 0.5)"
                                : hasCursor
                                  ? "0 0 10px rgba(59, 130, 246, 0.5)"
                                  : "none"
                        }
                      >
                        <p>{user.name.substring(0, 1).toUpperCase()}</p>
                      </IconButton>
                      </div>
                      {isCurrentUser && (
                        <div style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          backgroundColor: '#fbbf24',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}>
                          <FontAwesomeIcon
                            icon={faStar}
                            style={{
                              color: 'white',
                              fontSize: '10px'
                            }}
                          />
                        </div>
                      )}
                      {isStale && !isCurrentUser && (
                        <div style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          backgroundColor: STALE_BADGE_BG,
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `2px solid ${STALE_YELLOW_BORDER}`,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                        }}>
                          <FontAwesomeIcon
                            icon={faClock}
                            style={{
                              color: '#713f12',
                              fontSize: '10px'
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </Tooltip>
                );
              })}
              <Badge variant="solid">+ {usersToRender.length - maxAvatars} more</Badge>
            </>
          ) : (
            // Show all avatars
            usersToRender.map((user) => {
              const isOnPatientDetailsPage = !user.document_path || !user.document_path.endsWith('.json');
              const isStale = user.isStale === true;
              const isYouOnThisDocPage =
                viewerContext === 'document' &&
                viewerDocPath.length > 0 &&
                resolvedCurrentUserId &&
                user.user_id === resolvedCurrentUserId;
              const shouldShowAsInactive =
                showAsInactive ||
                (!isStale &&
                  !isYouOnThisDocPage &&
                  (isOnPatientDetailsPage || (isViewerPatientPage && !user.document_path)));
              
              const localColor = userColors && userColors.get ? userColors.get(user.user_id) : null;
              const cachedColor = localColor || globalPresenceManager.getCachedUserColor(user.user_id);
              const baseCollaborationColor = shouldShowAsInactive ? "#3b82f6" : (cachedColor || "#3b82f6");
              const userColor =
                isStale && !shouldShowAsInactive
                  ? lightenHexTowardWhite(baseCollaborationColor, 0.48)
                  : baseCollaborationColor;

              // Debug logging for color retrieval
              if (!cachedColor && !shouldShowAsInactive) {
                avLog('⚠️ AvatarDisplay: No color found for user:', {
                  userId: user.user_id,
                  userName: user.name,
                  documentPath: user.document_path,
                  localColor,
                  cachedColor: globalPresenceManager.getCachedUserColor(user.user_id),
                  allCachedColors: Array.from(globalPresenceManager.getCachedUserColors().entries())
                });
              }
              const isTyping = shouldShowAsInactive ? false : (user.is_typing === true);
              const hasCursor = shouldShowAsInactive ? false : (user.cursor_active === true);
              const isCurrentUser = resolvedCurrentUserId && user.user_id === resolvedCurrentUserId;

              return (
                <Tooltip
                  key={user.user_id}
                  label={
                    <VStack spacing={1} align="start">
                      <Text>{user.name} {isCurrentUser && "(You)"}</Text>
                      {isStale && (
                        <Text fontSize="xs" color="gray.600">
                          Left document — may return (grace period)
                        </Text>
                      )}
                      {!shouldShowAsInactive && isTyping && !isStale && <Text fontSize="xs" color="green.300">Typing...</Text>}
                      {!shouldShowAsInactive && hasCursor && !isTyping && !isStale && <Text fontSize="xs" color="blue.300">Active</Text>}
                      {shouldShowAsInactive && <Text fontSize="xs" color="gray.400">Inactive</Text>}
                    </VStack>
                  }
                  openDelay={250}
                >
                  <div className="my-2" style={{ position: 'relative' }}>
                    <div
                      style={
                        isStale && !shouldShowAsInactive
                          ? {
                              display: 'inline-flex',
                              lineHeight: 0,
                              borderRadius: '50%',
                              padding: '2px',
                              border: `2px dashed ${STALE_YELLOW_BORDER}`,
                              boxShadow: '0 0 10px rgba(234, 179, 8, 0.45)',
                            }
                          : { display: 'inline-flex', lineHeight: 0 }
                      }
                    >
                    <IconButton
                      size="md"
                      className="p-8"
                      fontSize="25px"
                      rounded="full"
                      variant="solid"
                      bg={userColor}
                      color="white"
                      onClick={() => {
                        const allUsersSnapshot = mergePresenceSources(
                          globalPresenceManager.getActiveUsers(),
                          getUsersFromPresenceChannelSnapshot()
                        );
                        const patientIdForLog = String(currentPatientId || '').trim();
                        const patientNameForLog = (patientName || '').toLowerCase().trim();
                        const stageBaseFiltered = getFilteredUsersForContext(
                          allUsersSnapshot,
                          currentPatientId,
                          patientName,
                          scheduleDate
                        );
                        const hasExplicitCurrentPatientUser = allUsersSnapshot.some((u) => {
                          const path = String(u?.document_path || '');
                          const pid = String(u?.patient_id || '').trim();
                          return pid === patientIdForLog || path.startsWith(`patient/${patientIdForLog}`);
                        });
                        const unknownContextUsers = allUsersSnapshot.filter((u) => {
                          const path = String(u?.document_path || '');
                          const pid = String(u?.patient_id || '').trim();
                          return path.length === 0 && pid.length === 0;
                        });
                        const matchDebug = allUsersSnapshot.map((u) => ({
                          userId: u.user_id,
                          name: u.name,
                          patientId: u.patient_id,
                          documentPath: u.document_path,
                          ...getPatientMatchReasons(u, patientIdForLog, patientNameForLog, scheduleDate),
                          includedInBaseFilter: stageBaseFiltered.some((x) => x.user_id === u.user_id),
                          includedInRenderedList: usersToRender.some((x) => x.user_id === u.user_id),
                          hasUnknownContext:
                            String(u?.document_path || '').length === 0 &&
                            String(u?.patient_id || '').trim().length === 0,
                          isStale: u.isStale === true,
                          cachedColor: globalPresenceManager.getCachedUserColor(u.user_id),
                        }));
                        console.groupCollapsed('🧪 [Avatar Click Debug]');
                        console.log('clickedUser', user);
                        console.log('viewer', {
                          viewerContext,
                          currentPatientId,
                          patientName,
                          scheduleDate,
                          viewerVisitDay: normalizeVisitDate(scheduleDate) || null,
                          resolvedCurrentUserId,
                        });
                        console.log(
                          'presenceHealth',
                          typeof globalPresenceManager.getPresenceHealth === 'function'
                            ? globalPresenceManager.getPresenceHealth()
                            : null,
                        );
                        console.log('renderState', {
                          activeUsersCount: activeUsers.length,
                          usersToRenderCount: usersToRender.length,
                          activeUsers,
                          usersToRender,
                          typingUsers,
                          userColors: Array.from(userColors?.entries?.() || []),
                        });
                        console.log('filterStages', {
                          allUsersCount: allUsersSnapshot.length,
                          baseFilteredCount: stageBaseFiltered.length,
                          baseFilteredUserIds: stageBaseFiltered.map((x) => x.user_id),
                          hasExplicitCurrentPatientUser,
                          unknownContextUsersCount: unknownContextUsers.length,
                        });
                        console.log('presenceSnapshot', matchDebug);
                        try {
                          console.table(matchDebug);
                        } catch (_) {
                          // console.table may be unavailable in some environments
                        }
                        console.groupEnd();
                        avLog('🖱️ Avatar clicked:', user, 'isStale:', isStale);
                        if (onUserAvatarClick) {
                          onUserAvatarClick(user);
                        }
                      }}
                      cursor="pointer"
                      opacity={1}
                      _hover={{
                        transform: 'scale(1.1)',
                        bg: userColor,
                        opacity: 1
                      }}
                      border={
                        shouldShowAsInactive
                          ? "none"
                          : isStale
                            ? "none"
                            : isTyping
                              ? "2px solid #10b981"
                              : hasCursor
                                ? "2px solid #3b82f6"
                                : "none"
                      }
                      boxShadow={
                        shouldShowAsInactive
                          ? "none"
                          : isStale
                            ? "none"
                            : isTyping
                              ? "0 0 10px rgba(16, 185, 129, 0.5)"
                              : hasCursor
                                ? "0 0 10px rgba(59, 130, 246, 0.5)"
                                : "none"
                      }
                    >
                      <p>{user.name.substring(0, 1).toUpperCase()}</p>
                    </IconButton>
                    </div>
                    {isCurrentUser && (
                      <div style={{
                        position: 'absolute',
                        top: '-5px',
                        right: '-5px',
                        backgroundColor: '#fbbf24',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}>
                        <FontAwesomeIcon
                          icon={faStar}
                          style={{
                            color: 'white',
                            fontSize: '10px'
                          }}
                        />
                      </div>
                    )}
                    {isStale && !isCurrentUser && (
                      <div style={{
                        position: 'absolute',
                        top: '-5px',
                        right: '-5px',
                        backgroundColor: STALE_BADGE_BG,
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `2px solid ${STALE_YELLOW_BORDER}`,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                      }}>
                        <FontAwesomeIcon
                          icon={faClock}
                          style={{
                            color: '#713f12',
                            fontSize: '10px'
                          }}
                        />
                      </div>
                    )}
                  </div>
                </Tooltip>
              );
            })
          )}
        </>
      )}
    </div>
  );
}
